'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentViewer, GeneralNotFound } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, EventEmitter, guardedClient as request } from '@repo/shared/helpers';
import { getAllGroupTasks } from 'src/api/admin/tasks';
import { getGroupDocumentTypes } from 'src/api/admin/documentTypes';
import { getGroupDocument } from 'src/api/admin/documents';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/groups/$groupId/documents/$documentId')({
	loader: async ({ params }) => {
		const { groupId, documentId } = params;
		const [document, documentTypes, groupTasks] = await Promise.all([
			getGroupDocument(groupId, documentId),
			getGroupDocumentTypes(),
			getAllGroupTasks(groupId),
		]);
		return { document, documentTypes, groupTasks };
	},
	component: DocumentDetailPage,
});

function DocumentDetailPage() {
	const { groupId, documentId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		document: initialDocument,
		documentTypes: initialDocumentTypes,
		groupTasks: initialGroupTasks,
	} = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: document, refetch: refetchDocument } = useQuery({
		queryKey: ['group-document', groupId, documentId],
		queryFn: () => getGroupDocument(groupId, documentId),
		initialData: initialDocument,
	});

	const { data: documentTypes } = useQuery({
		queryKey: ['group-document-types'],
		queryFn: () => getGroupDocumentTypes(),
		initialData: initialDocumentTypes,
	});

	const { data: groupTasks } = useQuery({
		queryKey: ['group-all-tasks', groupId],
		queryFn: () => getAllGroupTasks(groupId),
		initialData: initialGroupTasks,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleUpdate = () => {
		refetchDocument();
		queryClient.invalidateQueries({ queryKey: ['group-documents', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['groups'] });
	};

	const handleClose = () => {
		navigate({ to: '/groups/$groupId/documents', params: { groupId }, search: { filter: undefined } });
	};

	const forceParseDoc = async (doc?: typeof document) => {
		if (!doc) return;
		try {
			await request.post(`/api/groups/${groupId}/files/${doc._id}/force-reparse`, doc);
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to update document.',
			});
		}
		handleUpdate();
	};

	if (!document) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/groups/${groupId}/documents`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={documentTypes?.data || []}
			apiUrlBase={`/api/groups/${groupId}/files`}
			onRevalidateDocument={handleUpdate}
			onForceParse={authenticatedUser?.isSystemAdmin ? forceParseDoc : undefined}
			tasks={groupTasks || []}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.adminGroupsView]),
				fullView: verifyPermission([PERMISSIONS.adminGroupsView]),
				sensitiveView: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
				edit: verifyPermission([PERMISSIONS.adminGroupsEdit]),
				archive: verifyPermission([PERMISSIONS.adminGroupsEdit]),
				review: verifyPermission([PERMISSIONS.adminGroupsReview]),
			}}
		/>
	);
}
